import { useQuery } from "@tanstack/react-query";
import { Button, Input, Page, Select } from "@theg1group/ui";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GraphView } from "../components/GraphView.tsx";
import { api } from "../lib/api.ts";
import type { PartnerRow, Rows } from "../lib/types.ts";

export function GraphPage() {
	const [params, setParams] = useSearchParams();
	const focus = params.get("focus") ?? "";
	const depth = Number(params.get("depth") ?? "1");
	const minUsd = Number(params.get("minUsd") ?? "0");

	const [mode, setMode] = useState<"partner" | "address">(
		focus.startsWith("address:") ? "address" : "partner",
	);
	const [partnerId, setPartnerId] = useState(focus.startsWith("partner:") ? focus.slice(8) : "");
	const [addressId, setAddressId] = useState(focus.startsWith("address:") ? focus.slice(8) : "");
	const [depthInput, setDepthInput] = useState(String(depth));
	const [minUsdInput, setMinUsdInput] = useState(String(minUsd));

	const partners = useQuery({
		queryKey: ["partners", "", ""],
		queryFn: () => api.get<Rows<PartnerRow>>("/api/partners?q=&status="),
	});

	function apply(nextFocus?: string) {
		const f =
			nextFocus ??
			(mode === "partner"
				? partnerId
					? `partner:${partnerId}`
					: ""
				: addressId
					? `address:${addressId.trim()}`
					: "");
		const next: Record<string, string> = {};
		if (f) next.focus = f;
		next.depth = String(Math.min(3, Math.max(1, Number(depthInput) || 1)));
		if (Number(minUsdInput) > 0) next.minUsd = String(Number(minUsdInput));
		setParams(next);
		if (nextFocus) {
			if (nextFocus.startsWith("partner:")) {
				setMode("partner");
				setPartnerId(nextFocus.slice(8));
			} else {
				setMode("address");
				setAddressId(nextFocus.slice(8));
			}
		}
	}

	return (
		<Page
			title="Flow Graph"
			description="Partners, their wallets and counterparties. Diamonds are partners; colour = layer."
			actions={
				<form
					onSubmit={(e) => {
						e.preventDefault();
						apply();
					}}
					className="flex flex-wrap gap-2 items-end"
				>
					<div className="w-28">
						<Select
							value={mode}
							onChange={(e) => setMode(e.target.value as "partner" | "address")}
							options={[
								{ value: "partner", label: "Partner" },
								{ value: "address", label: "Address" },
							]}
							size="sm"
						/>
					</div>
					{mode === "partner" ? (
						<div className="w-56">
							<Select
								value={partnerId}
								onChange={(e) => setPartnerId(e.target.value)}
								options={[
									{ value: "", label: "All partners" },
									...(partners.data?.rows ?? []).map((p) => ({ value: p.id, label: p.name })),
								]}
								size="sm"
							/>
						</div>
					) : (
						<div className="w-72">
							<Input
								value={addressId}
								onChange={(e) => setAddressId(e.target.value)}
								placeholder="Address id (uuid)"
								className="font-mono"
								size="sm"
							/>
						</div>
					)}
					<div className="w-20">
						<Input
							type="number"
							min={1}
							max={3}
							value={depthInput}
							onChange={(e) => setDepthInput(e.target.value)}
							size="sm"
							title="Depth 1–3"
						/>
					</div>
					<div className="w-28">
						<Input
							type="number"
							min={0}
							value={minUsdInput}
							onChange={(e) => setMinUsdInput(e.target.value)}
							placeholder="Min USD"
							size="sm"
						/>
					</div>
					<Button type="submit" size="sm" variant="primary">
						Apply
					</Button>
				</form>
			}
		>
			<GraphView
				focus={focus || undefined}
				depth={depth}
				minUsd={minUsd}
				onFocus={(f) => apply(f)}
			/>
		</Page>
	);
}
